import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
	FiCheck,
	FiCheckCircle,
	FiCopy,
	FiDownload,
	FiEdit2,
	FiExternalLink,
	FiFileText,
	FiLink,
	FiSearch,
	FiTrash2,
	FiX,
	FiZap
} from "react-icons/fi";
import type { StorageObject, Taxonomie } from "../../API";
import { client } from "../../Clients";
import GatedButton from "../GatedButton";
import { LoadingSpinner } from "../LoadingSpinner";
import { Modal } from "../Modal";
import TaxonomyMultiSelect from "../TaxonomyMultiSelect";
import { formatExactDateFr, formatRelativeTimeFr } from "../../Helper/dates";
import { FileThumb, fileKindVisuals } from "./BrowserView";
import type { UsageScanState } from "./types";
import {
	buildPublicUrl,
	editorPathForUsage,
	extractErrorMessage,
	fileKindLabel,
	findUsageEntry,
	formatFileSize,
	resolveCompressedKey,
	resolveDisplayKey,
	resolveFileKind,
	resolveOriginalKey,
	triggerBlobDownload
} from "./utils";

const WRITE_TOKEN = "feature:storage:write";

/** Libellés FR des schemaKey les plus courants du « Utilisé par ». */
const SCHEMA_LABELS: Record<string, string> = {
	page: "Page",
	user: "Avatar utilisateur"
};

// ---------------------------------------------------------------------------
// Petites briques
// ---------------------------------------------------------------------------

/** Une clé du storage copiable en un clic (icône copie → coche 1,5 s). */
const CopyableKey: React.FC<{ label: string; value: string | null }> = ({ label, value }) => {
	const [copied, setCopied] = useState(false);

	const copy = async () => {
		if (!value) return;
		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(value);
			} else {
				// Repli hors HTTPS/localhost : textarea invisible + execCommand.
				const area = document.createElement("textarea");
				area.value = value;
				area.style.position = "fixed";
				area.style.opacity = "0";
				document.body.appendChild(area);
				area.select();
				document.execCommand("copy");
				area.remove();
			}
			setCopied(true);
			window.setTimeout(() => setCopied(false), 1500);
		} catch {
			toast.error("Impossible de copier la clé.");
		}
	};

	return (
		<div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/70 px-2.5 py-1.5">
			<span className="w-20 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
			<span className="min-w-0 flex-1 truncate font-mono text-xs text-slate-700" title={value ?? undefined}>
				{value ?? "-"}
			</span>
			{value && (
				<button
					type="button"
					onClick={copy}
					title={`Copier la clé ${label}`}
					aria-label={`Copier la clé ${label}`}
					className="shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
				>
					{copied ? <FiCheck className="h-3.5 w-3.5 text-emerald-500" /> : <FiCopy className="h-3.5 w-3.5" />}
				</button>
			)}
		</div>
	);
};

const InfoCell: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
		<p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
		<p className="mt-0.5 truncate text-xs font-semibold text-slate-800">{children}</p>
	</div>
);

const SectionTitle: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
	<p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
		{icon} {children}
	</p>
);

// ---------------------------------------------------------------------------
// Section « Utilisé par »
// ---------------------------------------------------------------------------

const UsageSection: React.FC<{
	object: StorageObject;
	usage: UsageScanState;
	onRequestScan: () => void;
}> = ({ object, usage, onRequestScan }) => {
	const entry = useMemo(() => findUsageEntry(usage.report, object), [usage.report, object]);

	return (
		<div className="space-y-2">
			<SectionTitle icon={<FiLink aria-hidden />}>Utilisé par</SectionTitle>
			{!usage.report && !usage.loading && (
				<>
					<p className="text-xs text-slate-500">
						Analyse toute la médiathèque (une seule fois, résultat gardé en mémoire) pour savoir quels contenus
						référencent ce fichier.
					</p>
					<button
						type="button"
						onClick={onRequestScan}
						className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						<FiSearch className="h-3.5 w-3.5" /> Analyser l'utilisation
					</button>
				</>
			)}
			{usage.loading && (
				<p className="flex items-center gap-2 text-xs text-slate-500">
					<LoadingSpinner size="sm" inline /> Analyse de la médiathèque en cours…
				</p>
			)}
			{usage.error && !usage.loading && (
				<p className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700" role="alert">
					{usage.error}
				</p>
			)}
			{usage.report && !usage.loading && (
				<>
					{!entry ? (
						<p className="text-xs text-slate-500">Fichier absent du dernier rapport : relancez l'analyse après un ajout récent.</p>
					) : entry.verdict === "orphan" ? (
						<p className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
							<span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden /> Orphelin : aucun contenu ne le référence
						</p>
					) : (
						<div className="space-y-1.5">
							<p className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
								<FiCheckCircle className="h-3.5 w-3.5" aria-hidden /> Utilisé
							</p>
							{(entry.usedBy?.length ?? 0) > 0 ? (
								<ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
									{entry.usedBy!.map((ref) => {
										const to = editorPathForUsage(ref);
										const label = SCHEMA_LABELS[ref.schemaKey] ?? ref.schemaKey;
										return (
											<li key={`${ref.schemaKey}-${ref.contentId}`} className="flex items-center gap-2 px-2.5 py-1.5">
												<span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
													{label}
												</span>
												{to ? (
													<Link
														to={to}
														className="inline-flex min-w-0 items-center gap-1 truncate font-mono text-xs font-semibold text-[#005CA9] hover:underline"
														title="Ouvrir dans l'éditeur"
													>
														<span className="truncate">{ref.contentId}</span>
														<FiExternalLink className="h-3 w-3 shrink-0" aria-hidden />
													</Link>
												) : (
													<span className="truncate font-mono text-xs text-slate-600">{ref.contentId}</span>
												)}
											</li>
										);
									})}
								</ul>
							) : (
								<p className="text-xs text-slate-500">Référencé par le système (ex. logo par défaut) : aucun contenu individuel.</p>
							)}
							{(entry.usedBy?.length ?? 0) >= 5 && (
								<p className="text-[11px] text-slate-400">Les 5 premiers contenus référencés sont listés.</p>
							)}
						</div>
					)}
				</>
			)}
		</div>
	);
};

// ---------------------------------------------------------------------------
// Le panneau de détail
// ---------------------------------------------------------------------------

interface DetailPanelProps {
	object: StorageObject;
	taxonomies: Taxonomie[];
	usage: UsageScanState;
	onRequestScan: () => void;
	onClose: () => void;
	/** Métadonnées / taxonomies / recompression : l'objet mis à jour remonte au parent. */
	onObjectUpdated: (updated: StorageObject) => void;
	onDeleted: (object: StorageObject) => void;
}

/**
 * Dossier complet du fichier sélectionné : aperçu, les trois clés copiables, métadonnées et
 * taxonomies éditables (PUT /api/storage/{id} et /{id}/taxonomies), téléchargement streamé par
 * l'API (GET /{id}/download), recompression (images) et suppression : toutes les écritures passent
 * par GatedButton feature:storage:write, et la suppression exige une confirmation explicite.
 */
export const DetailPanel: React.FC<DetailPanelProps> = ({
	object,
	taxonomies,
	usage,
	onRequestScan,
	onClose,
	onObjectUpdated,
	onDeleted
}) => {
	const [editingMeta, setEditingMeta] = useState(false);
	const [metaTitle, setMetaTitle] = useState("");
	const [metaFileName, setMetaFileName] = useState("");
	const [metaSaving, setMetaSaving] = useState(false);
	const [taxonomyEditing, setTaxonomyEditing] = useState(false);
	const [taxonomyIds, setTaxonomyIds] = useState<string[]>([]);
	const [taxonomySaving, setTaxonomySaving] = useState(false);
	const [recompressing, setRecompressing] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [deleting, setDeleting] = useState(false);

	// Changement de sélection → toute édition en cours est abandonnée (le panneau repart du nouvel objet).
	useEffect(() => {
		setEditingMeta(false);
		setMetaTitle(object.title ?? "");
		setMetaFileName(object.fileName ?? "");
		setTaxonomyEditing(false);
		setTaxonomyIds((object.taxonomyIds ?? []).filter((id): id is string => Boolean(id && id.trim())).map((id) => id.trim()));
		setConfirmDelete(false);
	}, [object]);

	const kind = resolveFileKind(object);
	const displayKey = resolveDisplayKey(object);
	const originalKey = resolveOriginalKey(object);
	const compressedKey = resolveCompressedKey(object);
	const assetUrl = displayKey ? buildPublicUrl(displayKey) : null;
	const originalUrl = originalKey ? buildPublicUrl(originalKey) : null;

	const taxonomyMap = useMemo(() => {
		const map = new Map<string, string>();
		taxonomies.forEach((taxonomie) => {
			if (taxonomie.id) map.set(taxonomie.id, taxonomie.title ?? taxonomie.id);
		});
		return map;
	}, [taxonomies]);

	const saveMetadata = async () => {
		if (!object.id || metaSaving) return;
		const title = metaTitle.trim();
		if (!title) {
			toast.error("Le titre est obligatoire.");
			return;
		}
		setMetaSaving(true);
		try {
			const response = await client.storage.apiStorageIdPut(object.id, {
				title,
				fileName: metaFileName.trim() || null,
				taxonomyIds: null
			});
			onObjectUpdated(response.data ?? { ...object, title, fileName: metaFileName.trim() || object.fileName });
			setEditingMeta(false);
			toast.success("Métadonnées enregistrées.");
		} catch (error) {
			console.error("Échec de la mise à jour des métadonnées", error);
			toast.error(extractErrorMessage(error) ?? "Impossible d'enregistrer les métadonnées.");
		} finally {
			setMetaSaving(false);
		}
	};

	const saveTaxonomies = async () => {
		if (!object.id || taxonomySaving) return;
		setTaxonomySaving(true);
		try {
			const response = await client.storage.apiStorageIdTaxonomiesPut(object.id, { taxonomyIds });
			onObjectUpdated(response.data ?? { ...object, taxonomyIds });
			setTaxonomyEditing(false);
			toast.success("Taxonomies mises à jour.");
		} catch (error) {
			console.error("Échec de la mise à jour des taxonomies", error);
			toast.error(extractErrorMessage(error) ?? "Impossible de mettre à jour les taxonomies.");
		} finally {
			setTaxonomySaving(false);
		}
	};

	const recompress = async () => {
		if (!object.id || recompressing) return;
		setRecompressing(true);
		try {
			const response = await client.storage.apiStorageIdRecompressPost(object.id);
			onObjectUpdated(response.data ?? object);
			toast.success("Image recompressée.");
		} catch (error) {
			console.error("Échec de la recompression", error);
			toast.error(extractErrorMessage(error) ?? "Impossible de recompresser l'image.");
		} finally {
			setRecompressing(false);
		}
	};

	const download = async () => {
		if (!object.id || downloading) return;
		setDownloading(true);
		try {
			// Le binaire est streamé par l'API admin (clé interne), pas par l'URL publique : // fonctionne donc aussi pour les fichiers dont le bucket public n'a pas de copie.
			const response = await client.storage.apiStorageIdDownloadGet(object.id, { responseType: "blob" });
			const blob = response.data as unknown as Blob;
			triggerBlobDownload(blob, object.fileName ?? displayKey?.split("/").pop() ?? "fichier");
		} catch (error) {
			console.error("Échec du téléchargement", error);
			toast.error(extractErrorMessage(error) ?? "Impossible de télécharger le fichier.");
		} finally {
			setDownloading(false);
		}
	};

	const remove = async () => {
		if (!object.id || deleting) return;
		setDeleting(true);
		try {
			await client.storage.apiStorageIdDelete(object.id);
			toast.success("Fichier supprimé du stockage.");
			setConfirmDelete(false);
			onDeleted(object);
		} catch (error) {
			// 409 = le serveur a REFUSÉ : le fichier est encore référencé par du contenu,
			// archives comprises. On nomme les contenus pour que l'opérateur sache où agir.
			const resp = (error as { response?: { status?: number; data?: { error?: string; usages?: { contentId?: string; schemaKey?: string; isArchived?: boolean }[] } } })?.response;
			if (resp?.status === 409 && resp.data?.error === "media_in_use") {
				const usages = resp.data.usages ?? [];
				const names = usages
					.slice(0, 4)
					.map((u) => `${u.schemaKey ?? "contenu"}/${u.contentId ?? "?"}${u.isArchived ? " (archivé)" : ""}`)
					.join(", ");
				toast.error(
					`Suppression refusée : ce fichier est utilisé par ${usages.length} contenu${usages.length > 1 ? "s" : ""}` +
					`${names ? ` (${names}${usages.length > 4 ? ", …" : ""})` : ""}. Détachez-le d'abord, archives comprises.`,
					{ autoClose: 10000 }
				);
				setConfirmDelete(false);
			} else {
				console.error("Échec de la suppression", error);
				toast.error(extractErrorMessage(error) ?? "Impossible de supprimer le fichier.");
			}
		} finally {
			setDeleting(false);
		}
	};

	const { accentClass, icon } = fileKindVisuals(kind);

	return (
		<aside className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" aria-label="Détail du fichier">
			{/* ---- En-tête : type + titre + fermer ---- */}
			<div className="flex items-start gap-3">
				<span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${accentClass} [&>svg]:h-5 [&>svg]:w-5`}>{icon}</span>
				<div className="min-w-0 flex-1">
					{editingMeta ? (
						<div className="space-y-2">
							<label className="block">
								<span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Titre</span>
								<input
									type="text"
									value={metaTitle}
									onChange={(e) => setMetaTitle(e.target.value)}
									className="mt-0.5 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-semibold text-slate-900 focus:border-slate-400 focus:outline-none"
								/>
							</label>
							<label className="block">
								<span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Nom de fichier</span>
								<input
									type="text"
									value={metaFileName}
									onChange={(e) => setMetaFileName(e.target.value)}
									className="mt-0.5 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 font-mono text-xs text-slate-700 focus:border-slate-400 focus:outline-none"
								/>
							</label>
							<div className="flex items-center gap-2">
								<GatedButton
									token={WRITE_TOKEN}
									onClick={saveMetadata}
									disabled={metaSaving}
									className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
								>
									{metaSaving ? <LoadingSpinner size="xs" inline /> : <FiCheck className="h-3.5 w-3.5" />} Enregistrer
								</GatedButton>
								<button
									type="button"
									onClick={() => setEditingMeta(false)}
									disabled={metaSaving}
									className="rounded-full border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
								>
									Annuler
								</button>
							</div>
						</div>
					) : (
						<>
							<p className="truncate text-sm font-semibold text-slate-900" title={object.title ?? undefined}>
								{object.title ?? object.fileName ?? "Sans titre"}
							</p>
							<p className="truncate font-mono text-xs text-slate-400" title={object.fileName ?? undefined}>
								{object.fileName ?? "-"}
							</p>
						</>
					)}
				</div>
				{!editingMeta && (
					<button
						type="button"
						onClick={() => setEditingMeta(true)}
						title="Modifier le titre et le nom de fichier"
						aria-label="Modifier le titre et le nom de fichier"
						className="shrink-0 rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
					>
						<FiEdit2 className="h-4 w-4" />
					</button>
				)}
				<button
					type="button"
					onClick={onClose}
					title="Fermer le panneau"
					aria-label="Fermer le panneau"
					className="shrink-0 rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
				>
					<FiX className="h-4 w-4" />
				</button>
			</div>

			{/* ---- Aperçu ---- */}
			{kind === "image" ? (
				<a href={assetUrl ?? undefined} target="_blank" rel="noreferrer" title="Ouvrir l'image en taille réelle">
					<FileThumb object={object} className="h-48" />
				</a>
			) : kind === "pdf" && assetUrl ? (
				<div className="overflow-hidden rounded-xl border border-slate-200">
					<iframe src={`${assetUrl}#view=FitH`} title="Aperçu PDF" loading="lazy" className="h-56 w-full bg-white" />
				</div>
			) : (
				<FileThumb object={object} className="h-32" />
			)}
			{assetUrl && (
				<a
					href={assetUrl}
					target="_blank"
					rel="noreferrer"
					className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#005CA9] hover:underline"
				>
					<FiExternalLink className="h-3.5 w-3.5" /> Ouvrir dans un nouvel onglet
				</a>
			)}

			{/* ---- Métadonnées ---- */}
			<div className="space-y-1.5 border-t border-slate-100 pt-3">
				<SectionTitle icon={<FiFileText aria-hidden />}>Métadonnées</SectionTitle>
				<div className="grid grid-cols-2 gap-2">
					<InfoCell label="Type">{object.contentType ?? fileKindLabel(kind)}</InfoCell>
					<InfoCell label="Taille">{formatFileSize(object.sizeBytes)}</InfoCell>
					<InfoCell label="Ajouté">
						<span title={formatExactDateFr(object.createdAt)}>{formatRelativeTimeFr(object.createdAt)}</span>
					</InfoCell>
					<InfoCell label="Modifié">
						<span title={formatExactDateFr(object.updatedAt)}>{formatRelativeTimeFr(object.updatedAt)}</span>
					</InfoCell>
					<InfoCell label="Ajouté par">
						{/* Attribution portée par la ligne SQL depuis peu : les fichiers plus
						    anciens (ou importés en masse) n'en ont pas : tiret simple. */}
						{(object as { uploadedByName?: string }).uploadedByName?.trim() ? (
							<span title={(object as { uploadedByEmail?: string }).uploadedByEmail || undefined}>
								{(object as { uploadedByName?: string }).uploadedByName}
							</span>
						) : (
							<span className="text-slate-400">-</span>
						)}
					</InfoCell>
				</div>
				{object.folder && (
					<InfoCell label="Dossier">
						<span className="font-mono">{object.folder}</span>
					</InfoCell>
				)}
			</div>

			{/* ---- Taxonomies ---- */}
			<div className="space-y-1.5 border-t border-slate-100 pt-3">
				<div className="flex items-center justify-between">
					<SectionTitle icon={<FiLink aria-hidden />}>Taxonomies</SectionTitle>
					{!taxonomyEditing ? (
						<button
							type="button"
							onClick={() => setTaxonomyEditing(true)}
							className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-50"
						>
							Modifier
						</button>
					) : (
						<div className="flex items-center gap-1.5">
							<GatedButton
								token={WRITE_TOKEN}
								onClick={saveTaxonomies}
								disabled={taxonomySaving}
								className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
							>
								{taxonomySaving ? "Enregistrement…" : "Enregistrer"}
							</GatedButton>
							<button
								type="button"
								onClick={() => setTaxonomyEditing(false)}
								disabled={taxonomySaving}
								className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-50"
							>
								Annuler
							</button>
						</div>
					)}
				</div>
				{taxonomyEditing ? (
					<TaxonomyMultiSelect
						options={taxonomies}
						value={taxonomyIds}
						onChange={setTaxonomyIds}
						placeholder="Rechercher une taxonomie"
						helper="Sélectionnez une ou plusieurs taxonomies pour ce fichier."
					/>
				) : (object.taxonomyIds?.length ?? 0) > 0 ? (
					<div className="flex flex-wrap gap-1.5">
						{object.taxonomyIds!.map((id) => (
							<span key={id} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
								{taxonomyMap.get(id) ?? id}
							</span>
						))}
					</div>
				) : (
					<p className="text-xs text-slate-400">Aucune taxonomie assignée.</p>
				)}
			</div>

			{/* ---- Utilisé par ---- */}
			<div className="border-t border-slate-100 pt-3">
				<UsageSection object={object} usage={usage} onRequestScan={onRequestScan} />
			</div>

			{/* ---- Clés techniques ---- */}
			<div className="space-y-1.5 border-t border-slate-100 pt-3">
				<SectionTitle icon={<FiFileText aria-hidden />}>Clés techniques</SectionTitle>
				<CopyableKey label="Clé" value={object.key?.trim() || null} />
				<CopyableKey label="Originale" value={originalKey} />
				<CopyableKey label="Compressée" value={compressedKey} />
			</div>

			{/* ---- Actions ---- */}
			<div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
				<button
					type="button"
					onClick={download}
					disabled={downloading || !object.id}
					className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{downloading ? <LoadingSpinner size="xs" inline /> : <FiDownload className="h-3.5 w-3.5" />} Télécharger
				</button>
				{kind === "image" && (
					<GatedButton
						token={WRITE_TOKEN}
						onClick={recompress}
						disabled={recompressing || !object.id}
						title="Régénère la copie compressée servie au site public"
						className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3.5 py-1.5 text-xs font-semibold text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{recompressing ? <LoadingSpinner size="xs" inline /> : <FiZap className="h-3.5 w-3.5" />} Recompresser
					</GatedButton>
				)}
				{originalUrl && originalUrl !== assetUrl && (
					<a
						href={originalUrl}
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
					>
						<FiExternalLink className="h-3.5 w-3.5" /> Original
					</a>
				)}
			</div>

			{/* ---- Zone danger ---- */}
			<div className="space-y-2 rounded-xl border border-rose-200 bg-rose-50/50 p-3">
				<p className="text-[11px] font-semibold uppercase tracking-wide text-rose-500">Zone danger</p>
				<p className="text-xs text-rose-700">
					La suppression retire le fichier du stockage et des buckets : irréversible. Vérifiez « Utilisé par » avant.
				</p>
				<GatedButton
					token={WRITE_TOKEN}
					onClick={() => setConfirmDelete(true)}
					disabled={!object.id}
					className="inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-rose-600 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<FiTrash2 className="h-3.5 w-3.5" /> Supprimer
				</GatedButton>
			</div>

			{/* ---- Confirmation de suppression ---- */}
			<Modal show={confirmDelete} onHide={() => (deleting ? undefined : setConfirmDelete(false))} size="sm" centered>
				<Modal.Header closeButton>
					<Modal.Title>Supprimer ce fichier ?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="space-y-2">
						<p className="text-sm text-slate-700">
							<span className="font-semibold">{object.title ?? object.fileName ?? "Ce fichier"}</span> sera retiré du
							stockage et des buckets. Cette action est irréversible.
						</p>
						<p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
							Vérifiez d'abord la section « Utilisé par » : supprimer un fichier encore référencé casse son affichage
							sur le site.
						</p>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setConfirmDelete(false)}
						disabled={deleting}
						className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={remove}
						disabled={deleting}
						className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{deleting ? <LoadingSpinner size="xs" inline /> : <FiTrash2 className="h-4 w-4" />} Supprimer définitivement
					</button>
				</Modal.Footer>
			</Modal>
		</aside>
	);
};
